// Map #7 — Floating Isles (owner 2026-11-02: 'get as creative as you can be with each'). A cluster of grassy sky isles
// fused into one broad disc and joined by old paved causeways, hanging over a cloud sea. The whole disc is solid ground:
// the void is only past the arena wall (and in the narrow glowing seams between the isles, which are painted cracks,
// never holes). Light, telegraphed hazards that hit the hero AND the foes:
//   • gale lanes (slow): long crosswind corridors that gust for a few seconds; streaks gather in them before they blow
//   • brittle spans (crush): cracked flagstone sections of the causeways that shake, glow, then drop away and mend later
// Final boss: the CLOUDHULL LEVIATHAN, a floating stone sky-whale that carries a little ruined isle on its back.
// Pure: no three, no DOM, only the seeded Rng it is handed (check:ip enforces it).
import type { HazardCtx, MapCoreDef, PropSpec } from '../../core/mapdef';
import type { HazardDef } from '../../core/hazards';
import { BUSH_VARIANTS, TREE_VARIANTS } from '../../core/terrain';

/** Isle mounds: broad, gentle plateaus (each reads as one isle of the cluster) with a few shallow mist hollows. */
export const ISLE_HILLS = { mounds: 14, dip: 0.2, rad: [18, 32] as [number, number], dipRad: [9, 15] as [number, number], amp: [1.1, 2.8] as [number, number], dipAmp: [0.5, 1.0] as [number, number], swell: 0.5, max: 3.0, min: -1.1 };

/** Prop archetypes as this map reskins them (render.ts): spire = sky monolith with a hovering crystal, boulder = fallen
 *  ruin block, rock = drifting crystal shard on a pebble, tree = wind-bent isle tree, bush = flowering shrub. */
export const ISLE_PROPS: PropSpec[] = [
  { kind: 'spire', count: 6, rMin: 26, inset: 8, size: [3.0, 4.4], clear: 14, h: [10, 16], hpPer: 4.5 },
  { kind: 'boulder', count: 20, rMin: 12, inset: 4, size: [1.4, 2.4], clear: 4, h: [0.8, 1.3], hpPer: 2 },
  { kind: 'tree', count: 66, rMin: 11, inset: 3, size: [0.7, 0.7], clear: 1.6, h: [1, 1], variants: TREE_VARIANTS },
  { kind: 'rock', count: 44, rMin: 9, inset: 2, size: [0.5, 0.9], clear: 1, h: [0.8, 1.2] },
  { kind: 'bush', count: 56, rMin: 10, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: BUSH_VARIANTS, layout: 'clumps' },
];

/** Hazard numbers (light; HAZARD_CAP clamps them anyway). */
export const GALE = { lanes: 4, halfLen: 20, halfWid: 4, power: 0.6, warn: 2.5, active: 7, start: 40, every: 38 } as const;
export const SPAN = { count: 5, halfLen: 5, halfWid: 2.6, power: 22, warn: 2.6, start: 75, every: 84 } as const;
/** nothing within this distance of the spawn (the hero starts at the centre plaza) */
export const SPAWN_CLEAR = 14;

/** gale lanes / brittle spans were authored on the 110 m arena; they scale with the arena area (owner 2026-11-02: 160 m) */
export const isleCounts = (arenaR: number): { lanes: number; spans: number } => {
  const k = Math.max(1, (arenaR / 110) ** 2);
  return { lanes: Math.round(GALE.lanes * k), spans: Math.round(SPAN.count * k) };
};

/** Hazard layout for one run (pure: ctx.rng only). */
export function isleHazards({ rng, arenaR, props }: HazardCtx): HazardDef[] {
  const out: HazardDef[] = [], C = isleCounts(arenaR), ring = arenaR / 110;
  // gale lanes: one per quadrant (per sector of the larger arena), across the radius (tangential) so they sweep over the causeways; staggered timers
  const a0 = rng.range(0, Math.PI * 2);
  for (let i = 0; i < C.lanes; i++) {
    const a = a0 + (i / C.lanes) * Math.PI * 2 + rng.range(-0.35, 0.35) * (GALE.lanes / C.lanes);
    // alternate lanes keep to the inner band (as at 110 m) and the outer band the larger arena added
    const outer = i % 2 === 1 && C.lanes > GALE.lanes, dMax = Math.min(80 * ring, arenaR - 30);
    const d = outer ? rng.range(80, dMax) : rng.range(SPAWN_CLEAR + GALE.halfWid + 22, Math.min(80, dMax));
    const rot = a + Math.PI / 2 + rng.range(-0.3, 0.3);
    out.push({ kind: 'slow', x: Math.cos(a) * d, z: Math.sin(a) * d, r: 0, l: GALE.halfLen, w: GALE.halfWid, rot, start: GALE.start + i * 11 * (GALE.lanes / C.lanes) + rng.range(0, 6),
      warn: GALE.warn, active: GALE.active, every: GALE.every + (i % GALE.lanes) * 4, power: GALE.power, tag: 'gale' });
  }
  // brittle spans: cracked causeway sections, kept off the spawn, apart from each other and clear of the big solid props
  const big = props.filter((p) => p.kind === 'spire' || p.kind === 'boulder');
  for (let i = 0, tries = 0; i < C.spans && tries < 400 * Math.ceil(C.spans / SPAN.count); tries++) {
    const a = rng.range(0, Math.PI * 2), d = rng.range(28, arenaR - 12), x = Math.cos(a) * d, z = Math.sin(a) * d;
    const reach = Math.hypot(SPAN.halfLen, SPAN.halfWid);
    if (out.some((h) => h.tag === 'span' && (h.x - x) ** 2 + (h.z - z) ** 2 < 30 * 30)) continue;
    if (big.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + reach + 2) ** 2)) continue;
    out.push({ kind: 'crush', x, z, r: 0, l: SPAN.halfLen, w: SPAN.halfWid, rot: a + rng.range(-0.4, 0.4), start: SPAN.start + i * 23 + rng.range(0, 8),
      warn: SPAN.warn, active: 0, every: SPAN.every + rng.range(0, 20), power: SPAN.power, tag: 'span' });
    i++;
  }
  return out;
}

export const MAP: MapCoreDef = {
  id: 'floating-isles',
  name: 'Floating Isles',
  tagline: 'Grass isles adrift above a sea of clouds, stitched together by old causeways. Gales cross them and cracked spans fall away.',
  order: 7,
  status: 'ready',
  // live cycle by default; 'Day' on this map means golden hour (the dusk look is a warm gold, not a red sunset)
  time: { default: 'cycle', day: 'dusk' },
  unlock: { after: 'ember-wastes', seconds: 600 },
  terrain: { hills: ISLE_HILLS, props: ISLE_PROPS },
  fire: true,
  nightPressure: 1.12,
  boss: { name: 'CLOUDHULL LEVIATHAN', hpMul: 1.1 },
  hazards: isleHazards,
};
